/** @jsx jsx */
import React, { memo, useCallback, useMemo, type SyntheticEvent } from 'react';
import { styled, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import { useExpandCollapseAllItems } from '@atlassian/jira-business-expand-collapse-all-items-controller/src/index.tsx';
import { useHideDoneItems } from '@atlassian/jira-business-filters/src/controllers/hide-done-items/index.tsx';
import { useCanCreateIssueInStatus } from '@atlassian/jira-business-issue-create/src/controllers/can-create-issue-in-status/index.tsx';
import {
	StatusCategoryTypes,
	StatusCategoryIds,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { gridSize, borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { getAriConfig } from '@atlassian/jira-platform-ari/src/index.tsx';
import { JiraIssueStatusAri } from '@atlassian/ari/jira/issue-status';
import {
	CATEGORY_TYPE,
	ASSIGNEE_TYPE,
	PRIORITY_TYPE,
	STATUS_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import CreateIssuePlusButton from '../../../common/ui/create-issue-plus-button/index.tsx';
import { useGetGroupByField } from '../../../controllers/get-group-by-field/index.tsx';
import { useIssueFields } from '../../../controllers/get-issue-fields/index.tsx';
import { useGroupBy } from '../../../controllers/group-by/index.tsx';
import { useHierarchyStore } from '../../../controllers/hierarchy/hierarchy-store/index.tsx';
import type { ListViewControllerData } from '../../../controllers/list-view-issues/types.tsx';
import { useRowCreateStore } from '../../../controllers/row-create/index.tsx';
import { AssigneeItem } from './assignee-item/index.tsx';
import { CategoryItem } from './category-item/index.tsx';
import { ExpandIcon } from './expand-icon/index.tsx';
import messages from './messages.tsx';
import { PriorityItem } from './priority-item/index.tsx';
import { SelectGroupCheckbox } from './select-group-checkbox/index.tsx';
import { SprintItem } from './sprint-item/index.tsx';
import { StatusItem } from './status-item/index.tsx';
import { StoryPointsItem } from './storypoints-item/index.tsx';
import { Wrapper, GroupContainer } from './styled.tsx';

const ICON_SIZE = 24;
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LoadingIcon = styled.div({
	display: 'flex',
	width: token('space.100'),
	height: `${ICON_SIZE}px`,

	backgroundColor: token('color.skeleton'),
	marginTop: token('space.100'),
	marginRight: token('space.050'),
	marginBottom: token('space.100'),
	marginLeft: token('space.200'),

	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('border.radius', '3px'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const CreateIssuePlusButtonWrapper = styled.div({
	marginLeft: token('space.100'),
});

type GroupItemProps = {
	data: ListViewControllerData;
	rowIndex?: number;
	canCreateIssues?: boolean;
};

const GroupItemRenderer = ({
	field,
	data,
}: {
	field: BusinessIssueField;
	data: ListViewControllerData;
}) => {
	switch (field.__typename) {
		case 'JiraSingleSelectUserPickerField':
			if (field.type === ASSIGNEE_TYPE) {
				return <AssigneeItem data={data} />;
			}
			return null;
		case 'GenericIssueField':
			if (field.type === STATUS_TYPE) {
				return <StatusItem data={data} />;
			}
			return null;
		case 'JiraPriorityField':
			return <PriorityItem data={data} />;
		case 'JWMCategoryField':
			return <CategoryItem data={data} />;
		case 'JiraNumberField':
			if (field.aliasFieldId === 'storypoints') {
				return <StoryPointsItem value={data.storypoints} />;
			}
			if (field.aliasFieldId === 'storypointestimate') {
				return <StoryPointsItem value={data.storypointestimate} />;
			}
			return null;
		case 'JiraSprintField':
			return <SprintItem data={data} />;
		default:
			return null;
	}
};

export const GroupItem = memo<GroupItemProps>(
	({ data, rowIndex, canCreateIssues }: GroupItemProps) => {
		const [groupByFieldId] = useGroupBy();
		const [, setHideDoneItems] = useHideDoneItems();
		const issueId = toIssueId(data.issueId);
		const totalGroupItems = data.childSummary?.totalCount ?? 0;
		const { formatMessage } = useIntl();
		const [
			{
				expandedItems: expandedGroups,
				hierarchyData: { loading },
			},
			{ setExpandedItems: setGroupExpanded },
		] = useHierarchyStore();
		const [{ shouldExpandAllItems }, { disableExpandCollapseAllItems }] = useExpandCollapseAllItems(
			{ view: 'list' },
		);

		const [, { setRowCreatePosition }] = useRowCreateStore();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { getAvailableFields } = useIssueFields();
		const availableFields = getAvailableFields();
		const categoryFieldId = availableFields.find(
			(fieldItem) => fieldItem.type === CATEGORY_TYPE,
		)?.id;

		const field = useGetGroupByField();

		const onExpand = useCallback(() => {
			const isExpanded = Boolean(expandedGroups[issueId]);
			const isStatusGroup = groupByFieldId === STATUS_TYPE;
			const isDoneStatus =
				// @ts-expect-error - TS2367 - This condition will always return 'false' since the types 'string' and 'number' have no overlap.
				data.status?.categoryId === StatusCategoryIds[StatusCategoryTypes.DONE];

			// show done items when expanding the done status group
			if (isStatusGroup && isDoneStatus && !isExpanded) {
				setHideDoneItems(false);
			}

			if (isExpanded && shouldExpandAllItems) {
				disableExpandCollapseAllItems();
			}
			setGroupExpanded(issueId, !isExpanded);
		}, [
			data,
			disableExpandCollapseAllItems,
			expandedGroups,
			groupByFieldId,
			issueId,
			setGroupExpanded,
			setHideDoneItems,
			shouldExpandAllItems,
		]);

		const handleAddInlineCreateRow = useCallback(
			(e: SyntheticEvent<HTMLButtonElement>) => {
				e.preventDefault();
				setRowCreatePosition({
					parentId: String(issueId),
				});
				if (setGroupExpanded) {
					setGroupExpanded(issueId, true);
				}
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'listRowCreateGroupItemIcon', {
					groupByFieldId,
				});
			},
			[setRowCreatePosition, issueId, setGroupExpanded, createAnalyticsEvent, groupByFieldId],
		);

		const checkIfHasIssueCreation = useCanCreateIssueInStatus();

		const canInlineCreate = useMemo(() => {
			if (!canCreateIssues) {
				return false;
			}

			if (groupByFieldId !== STATUS_TYPE) {
				return true;
			}

			let statusId = data.status?.id && getAriConfig(data.status.id).resourceId;

			if (fg('migrate-deprecated-jira-platform-ari-fns')) {
				statusId =
					data.status?.id && JiraIssueStatusAri.check(data.status.id)
						? JiraIssueStatusAri.parse(data.status.id).issuestatusId
						: undefined;
			}

			if (!statusId) {
				return false;
			}

			return checkIfHasIssueCreation(statusId);
		}, [canCreateIssues, checkIfHasIssueCreation, data.status, groupByFieldId]);

		const CreateIssueComponent = useMemo(
			() =>
				canInlineCreate && (
					<CreateIssuePlusButtonWrapper>
						<CreateIssuePlusButton
							tooltip={formatMessage(messages.createIssue)}
							label={formatMessage(messages.addGroupItem)}
							onClick={handleAddInlineCreateRow}
							shouldDisplayBtn
						/>
					</CreateIssuePlusButtonWrapper>
				),
			[canInlineCreate, formatMessage, handleAddInlineCreateRow],
		);

		if (loading) {
			return (
				<LoadingRow
					key="loading-grouping-header-row"
					data-testid="business-list.ui.list-view.group-item.loading-row"
				>
					<LoadingWrapper key={data.key}>
						<LoadingIcon />
						<LoadingField />
					</LoadingWrapper>
				</LoadingRow>
			);
		}

		const renderView = (
			<Wrapper>
				<SelectGroupCheckbox group={data} rowIndex={rowIndex} />

				<GroupContainer>
					{totalGroupItems > 0 && (
						<ExpandIcon
							onClick={onExpand}
							totalGroupItems={totalGroupItems}
							rowIndex={rowIndex}
							expanded={expandedGroups[issueId] === true}
						/>
					)}
					{field ? (
						<GroupItemRenderer field={field} data={data} />
					) : (
						<>
							{groupByFieldId === ASSIGNEE_TYPE && <AssigneeItem data={data} />}
							{groupByFieldId === STATUS_TYPE && <StatusItem data={data} />}
							{groupByFieldId === PRIORITY_TYPE && <PriorityItem data={data} />}
							{groupByFieldId === categoryFieldId && <CategoryItem data={data} />}
						</>
					)}
					{CreateIssueComponent}
				</GroupContainer>
			</Wrapper>
		);
		return renderView;
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LoadingWrapper = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LoadingField = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 15}px`,

	backgroundColor: token('color.skeleton'),
	marginTop: token('space.100'),
	marginRight: token('space.100'),
	marginBottom: token('space.100'),
	marginLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LoadingRow = styled.div({
	display: 'inline-flex',
});
